/* PreNumbers Financial Builder — charts (reskinned palette).
   REPLACES: src/components/FinancialBuilder/Charts/Charts.js

   Same exports, same data extraction from finalDATA.formattedData /
   finalDATA.percentageData. Only the visual styling changed: brand palette
   (teal / gold / coral / blue), subtle grid, dark panel tooltips. Reads CSS
   custom properties so it tracks the active theme. */

import React from "react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  Tooltip, Legend, CartesianGrid, LabelList, LineChart,
} from "recharts";

/* Brand palette */
const C = {
  teal: "#12c0bd",
  gold: "#f0be4d",
  coral: "#e7685e",
  blue: "#7aa2f7",
  green: "#10b981",
  grid: "rgba(255,255,255,0.08)",
  axis: "rgba(255,255,255,0.18)",
  tick: "#8a8f98",
  panel: "#191a1b",
  panelBorder: "rgba(255,255,255,0.12)",
  fg: "#f7f8f8",
};

const tooltipStyle = {
  backgroundColor: C.panel,
  border: `1px solid ${C.panelBorder}`,
  borderRadius: "8px",
  color: C.fg,
  fontSize: 13,
};
const legendStyle = { fontSize: 13, fontWeight: 500, color: C.tick };
const axisTick = { fontSize: 12, fill: C.tick };

const formatCurrency = (value) => `$${Number(value).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const formatPercentage = (value) => `${Number(value).toFixed(2)}%`;

export function RevenueVsCostChart({ finalDATA }) {
  const totalRevenueRow = finalDATA.formattedData.flat().find((row) => row[0] === "Total Revenue");
  const totalCostPercentRow = finalDATA.percentageData.flat().find((row) => row[0] === "Total Cost of Revenue %");
  if (!totalRevenueRow || !totalCostPercentRow) return <p>Missing Revenue vs Cost data.</p>;

  const chartData = totalRevenueRow.slice(1).map((rev, i) => ({
    year: `Year ${i}`,
    revenue: parseInt(String(rev).replace(/,/g, ""), 10),
    costPercent: parseFloat(String(totalCostPercentRow[i + 1]).replace("%", "")),
  }));

  return (
    <ResponsiveContainer style={{ margin: "0 auto" }} width="100%" height={360}>
      <ComposedChart data={chartData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
        <defs>
          <linearGradient id="cor-rev" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.teal} stopOpacity={0.95} />
            <stop offset="100%" stopColor={C.teal} stopOpacity={0.55} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
        <XAxis dataKey="year" tick={axisTick} axisLine={{ stroke: C.axis }} tickLine={false} />
        <YAxis yAxisId="left" tick={axisTick} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
        <YAxis yAxisId="right" orientation="right" tick={axisTick} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
        <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: C.fg, fontWeight: 600 }} cursor={{ fill: "rgba(255,255,255,0.04)" }}
          formatter={(value, name) => (name === "Revenue" ? formatCurrency(value) : `${value}%`)} />
        <Legend wrapperStyle={legendStyle} verticalAlign="top" align="left" height={28} />
        <Bar yAxisId="left" dataKey="revenue" fill="url(#cor-rev)" barSize={42} radius={[6, 6, 0, 0]} name="Revenue" />
        <Line yAxisId="right" type="monotone" dataKey="costPercent" stroke={C.gold} strokeWidth={2.5}
          dot={{ r: 4, fill: C.panel, stroke: C.gold, strokeWidth: 2 }} activeDot={{ r: 6 }} name="Cost of Revenue %" />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function FinancialProjectionChart({ finalDATA }) {
  const revenueRow = finalDATA.formattedData.flat().find((row) => row[0] === "Total Revenue");
  const costRow = finalDATA.formattedData.flat().find((row) => row[0] === "Total Cost");
  const incomeRow = finalDATA.formattedData.flat().find((row) => row[0] === "Net Income");
  if (!revenueRow || !costRow || !incomeRow) return <p>Missing Financial Projection data.</p>;

  const chartData = revenueRow.slice(1).map((rev, i) => ({
    year: `Year ${i}`,
    revenue: parseInt(String(rev).replace(/,/g, ""), 10),
    cost: parseInt(String(costRow[i + 1]).replace(/,/g, ""), 10),
    income: parseInt(String(incomeRow[i + 1]).replace(/,/g, ""), 10),
  }));

  const line = (key, name, color) => (
    <Line type="monotone" dataKey={key} name={name} stroke={color} strokeWidth={2.5}
      dot={{ r: 4, fill: C.panel, stroke: color, strokeWidth: 2 }} activeDot={{ r: 6 }} animationDuration={900}>
      <LabelList dataKey={key} position="top" formatter={(v) => `$${(v / 1e6).toFixed(1)}M`} style={{ fill: color, fontSize: 11, fontWeight: 600 }} />
    </Line>
  );

  return (
    <ResponsiveContainer style={{ margin: "0 auto" }} width="100%" height={380}>
      <LineChart data={chartData} margin={{ top: 24, right: 24, left: 8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
        <XAxis dataKey="year" tick={axisTick} axisLine={{ stroke: C.axis }} tickLine={false} />
        <YAxis tick={axisTick} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
        <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: C.fg, fontWeight: 600 }} formatter={(v) => formatCurrency(v)} />
        <Legend wrapperStyle={legendStyle} verticalAlign="top" align="left" height={28} />
        {line("revenue", "Total Revenue", C.teal)}
        {line("cost", "Total Cost", C.coral)}
        {line("income", "Net Income", C.gold)}
      </LineChart>
    </ResponsiveContainer>
  );
}

export function RevenueGrowthChart({ finalDATA }) {
  const linesData = finalDATA.percentageData[1];
  if (!linesData) return <p>Missing Revenue Growth data.</p>;
  const palette = [C.teal, C.gold, C.coral, C.blue, C.green];

  const chartData = linesData[0].slice(1).map((_, i) => ({ year: `Year ${i}` }));
  for (let i = 0; i < linesData.length; i++) {
    const lineName = linesData[i][0];
    chartData.forEach((dp, index) => { dp[lineName] = parseFloat(String(linesData[i][index + 1]).replace("%", "")); });
  }

  return (
    <ResponsiveContainer style={{ margin: "0 auto" }} width="100%" height={360}>
      <LineChart data={chartData} margin={{ top: 24, right: 24, left: 8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
        <XAxis dataKey="year" tick={axisTick} axisLine={{ stroke: C.axis }} tickLine={false} />
        <YAxis tick={axisTick} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
        <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: C.fg, fontWeight: 600 }} formatter={(v) => formatPercentage(v)} />
        <Legend wrapperStyle={legendStyle} verticalAlign="top" align="left" height={28} />
        {linesData.map((line, idx) => (
          <Line key={line[0]} type="monotone" dataKey={line[0]} stroke={palette[idx % palette.length]} strokeWidth={2.5}
            dot={{ r: 3.5, fill: C.panel, stroke: palette[idx % palette.length], strokeWidth: 2 }} activeDot={{ r: 6 }} animationDuration={900} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export function CostOverviewChart({ finalDATA }) {
  const fixedCostRow = finalDATA.formattedData.flat().find((row) => row[0] === "Fixed Cost");
  const variableCostRow = finalDATA.formattedData.flat().find((row) => row[0] === "Variable Cost");
  const totalCostRow = finalDATA.percentageData.flat().find((row) => row[0] === "Total Cost of Revenue %");
  const laborCostRow = finalDATA.percentageData.flat().find((row) => row[0] === "Labor Cost of Rev. %");
  if (!fixedCostRow || !variableCostRow || !totalCostRow || !laborCostRow) return <p>Missing Cost Overview data.</p>;

  const chartData = fixedCostRow.slice(1).map((_, i) => ({
    year: `Year ${i}`,
    fixedCost: parseInt(String(fixedCostRow[i + 1]).replace(/,/g, ""), 10),
    variableCost: parseInt(String(variableCostRow[i + 1]).replace(/,/g, ""), 10),
    totalCostPercent: parseFloat(String(totalCostRow[i + 1]).replace("%", "")),
    laborCostPercent: parseFloat(String(laborCostRow[i + 1]).replace("%", "")),
  }));

  return (
    <ResponsiveContainer style={{ margin: "0 auto" }} width="100%" height={360}>
      <ComposedChart data={chartData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
        <XAxis dataKey="year" tick={axisTick} axisLine={{ stroke: C.axis }} tickLine={false} />
        <YAxis yAxisId="left" tick={axisTick} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
        <YAxis yAxisId="right" orientation="right" tick={axisTick} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
        <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: C.fg, fontWeight: 600 }} cursor={{ fill: "rgba(255,255,255,0.04)" }}
          formatter={(value, name) => (name === "Fixed Cost" || name === "Variable Cost" ? formatCurrency(value) : formatPercentage(value))} />
        <Legend wrapperStyle={legendStyle} verticalAlign="top" align="left" height={28} />
        <Bar yAxisId="left" dataKey="fixedCost" fill={C.blue} radius={[6, 6, 0, 0]} name="Fixed Cost" barSize={26} />
        <Bar yAxisId="left" dataKey="variableCost" fill={C.teal} radius={[6, 6, 0, 0]} name="Variable Cost" barSize={26} />
        <Line yAxisId="right" dataKey="totalCostPercent" stroke={C.coral} strokeWidth={2.5} dot={{ r: 4, fill: C.panel, stroke: C.coral, strokeWidth: 2 }} name="Total Cost of Revenue %" />
        <Line yAxisId="right" dataKey="laborCostPercent" stroke={C.gold} strokeWidth={2.5} dot={{ r: 4, fill: C.panel, stroke: C.gold, strokeWidth: 2 }} name="Labor Cost %" />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function BurnRateChart({ finalDATA }) {
  const burnRateRow = finalDATA.formattedData.flat().find((row) => row[0] === "Monthly Burn Rate");
  const burnRatePercentRow = finalDATA.percentageData.flat().find((row) => row[0] === "Burn Rate Usage %");
  if (!burnRateRow || !burnRatePercentRow) return <p>Missing Burn Rate data.</p>;

  const chartData = burnRateRow.slice(1).map((_, i) => ({
    year: `Year ${i}`,
    burnRate: parseInt(String(burnRateRow[i + 1]).replace(/,/g, ""), 10),
    burnRatePercent: parseFloat(String(burnRatePercentRow[i + 1]).replace("%", "")),
  }));

  return (
    <ResponsiveContainer style={{ margin: "0 auto" }} width="100%" height={360}>
      <ComposedChart data={chartData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
        <defs>
          <linearGradient id="burn-bar" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.coral} stopOpacity={0.95} />
            <stop offset="100%" stopColor={C.coral} stopOpacity={0.5} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
        <XAxis dataKey="year" tick={axisTick} axisLine={{ stroke: C.axis }} tickLine={false} />
        <YAxis yAxisId="left" tick={axisTick} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1e3).toFixed(0)}k`} />
        <YAxis yAxisId="right" orientation="right" tick={axisTick} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
        <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: C.fg, fontWeight: 600 }} cursor={{ fill: "rgba(255,255,255,0.04)" }}
          formatter={(value, name) => (name === "Burn Rate" ? formatCurrency(value) : formatPercentage(value))} />
        <Legend wrapperStyle={legendStyle} verticalAlign="top" align="left" height={28} />
        <Bar yAxisId="left" dataKey="burnRate" fill="url(#burn-bar)" radius={[6, 6, 0, 0]} name="Burn Rate" barSize={40} />
        <Line yAxisId="right" dataKey="burnRatePercent" stroke={C.gold} strokeWidth={2.5} dot={{ r: 4, fill: C.panel, stroke: C.gold, strokeWidth: 2 }} name="Burn Rate Usage %" />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
